import { useTranslation } from 'react-i18next';

import { AccountLayout } from '@components/layout/AccountLayout';

const LINE_POINTS = [38, 52, 44, 68, 58, 74, 62, 80, 70, 85, 76, 90];
const BAR_A = [55, 80, 45, 70, 60, 90, 50, 75];
const BAR_B = [40, 65, 72, 50, 85, 60, 78, 55];

function KpiRow() {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      {[['w-20', 'w-24'], ['w-16', 'w-20'], ['w-24', 'w-16'], ['w-18', 'w-22']].map(([lw, vw], i) => (
        <div key={i} className="rounded-2xl border border-border bg-white/60 p-5">
          <div className={`mb-3 h-2 rounded-full bg-muted/55 ${lw}`} />
          <div className={`mb-1.5 h-7 rounded-lg bg-muted/45 ${vw}`} />
          <div className="flex items-center gap-1.5">
            <div className="h-2 w-2 rounded-full bg-primary/30" />
            <div className="h-2 w-10 rounded-full bg-muted/35" />
          </div>
        </div>
      ))}
    </div>
  );
}

function AreaChartGhost() {
  const w = 600;
  const h = 120;
  const pad = 4;
  const step = (w - pad * 2) / (LINE_POINTS.length - 1);

  const pts = LINE_POINTS.map((v, i) => ({
    x: pad + i * step,
    y: h - pad - (v / 100) * (h - pad * 2),
  }));

  const linePath = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');
  const areaPath = `${linePath} L${pts[pts.length - 1].x},${h} L${pts[0].x},${h} Z`;

  return (
    <div className="rounded-2xl border border-border bg-white/60 p-6">
      <div className="mb-1 flex items-center justify-between">
        <div className="h-2.5 w-28 rounded-full bg-muted/60" />
        <div className="flex gap-2">
          {['w-10', 'w-12', 'w-10'].map((w, i) => (
            <div key={i} className={`h-6 rounded-lg bg-muted/30 ${w}`} />
          ))}
        </div>
      </div>
      <div className="mb-4 h-2 w-16 rounded-full bg-muted/40" />
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full" preserveAspectRatio="none" style={{ height: 120 }}>
        <defs>
          <linearGradient id="rp-area-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor="hsl(160 51% 24%)" stopOpacity="0.18" />
            <stop offset="100%" stopColor="hsl(160 51% 24%)" stopOpacity="0.02" />
          </linearGradient>
        </defs>
        <path d={areaPath} fill="url(#rp-area-grad)" />
        <path d={linePath}  fill="none" stroke="hsl(160 51% 24%)" strokeWidth="2" strokeOpacity="0.4" strokeLinecap="round" strokeLinejoin="round" />
        {pts.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r="3" fill="hsl(160 51% 24%)" fillOpacity="0.35" />
        ))}
      </svg>
    </div>
  );
}

function BarChartGhost({ bars, label }: { bars: number[]; label: string }) {
  return (
    <div className="rounded-2xl border border-border bg-white/60 p-5">
      <div className={`mb-1 h-2.5 rounded-full bg-muted/60 ${label}`} />
      <div className="mb-4 h-2 w-12 rounded-full bg-muted/40" />
      <div className="flex h-24 items-end gap-1.5">
        {bars.map((h, i) => (
          <div
            key={i}
            className="flex-1 rounded-t-sm bg-primary/25"
            style={{ height: `${h}%` }}
          />
        ))}
      </div>
      <div className="mt-2 flex gap-1.5">
        {bars.map((_, i) => (
          <div key={i} className="flex-1 h-1.5 rounded-full bg-muted/25" />
        ))}
      </div>
    </div>
  );
}

export function ReportsPage() {
  const { t } = useTranslation();

  return (
    <AccountLayout>
      <div
        className="relative min-h-[calc(100vh-4rem)] overflow-hidden"
        style={{
          background:
            'radial-gradient(ellipse 70% 55% at 60% -5%, hsl(160 51% 24% / 0.08) 0%, transparent 70%), radial-gradient(ellipse 55% 45% at -5% 85%, hsl(160 51% 24% / 0.06) 0%, transparent 65%), hsl(var(--color-background))',
        }}
      >
        <div className="pointer-events-none absolute -right-24 -top-24 text-primary" aria-hidden="true">
          <svg width="480" height="480" viewBox="0 0 480 480" fill="none">
            <circle cx="240" cy="240" r="200" stroke="currentColor" strokeWidth="1"   opacity="0.08" />
            <circle cx="240" cy="240" r="150" stroke="currentColor" strokeWidth="1"   opacity="0.07" />
            <circle cx="240" cy="240" r="100" stroke="currentColor" strokeWidth="1.5" opacity="0.06" />
            <circle cx="240" cy="240" r="50"  stroke="currentColor" strokeWidth="2"   opacity="0.05" />
          </svg>
        </div>

        <div className="pointer-events-none absolute -bottom-40 -left-40 text-primary" aria-hidden="true">
          <svg width="400" height="400" viewBox="0 0 400 400" fill="none">
            <circle cx="200" cy="200" r="160" stroke="currentColor" strokeWidth="1"   opacity="0.06" />
            <circle cx="200" cy="200" r="110" stroke="currentColor" strokeWidth="1"   opacity="0.05" />
            <circle cx="200" cy="200" r="60"  stroke="currentColor" strokeWidth="1.5" opacity="0.04" />
          </svg>
        </div>

        <div className="relative mx-auto flex max-w-3xl flex-col items-center px-4 pb-20 pt-16 sm:pt-20">

          <div className="mb-6 flex h-[72px] w-[72px] items-center justify-center rounded-2xl bg-primary shadow-md">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>

          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-4 py-1.5">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary" />
            <span className="text-xs font-semibold uppercase tracking-widest text-primary">
              {t('layout.account.comingSoon')}
            </span>
          </div>

          <h1 className="mb-3 font-display text-5xl font-semibold text-foreground sm:text-6xl">
            {t('layout.account.nav.reports')}
          </h1>

          <p className="mb-14 max-w-xs text-center text-base leading-relaxed text-muted-foreground">
            Track revenue, appointments, and growth trends across your business over time.
          </p>

          <div className="w-full space-y-3 opacity-40">
            <KpiRow />
            <AreaChartGhost />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <BarChartGhost bars={BAR_A} label="w-24" />
              <BarChartGhost bars={BAR_B} label="w-20" />
            </div>
          </div>

        </div>
      </div>
    </AccountLayout>
  );
}
